// API client del cockpit clínico (MEDOS-16).
// Backend: apps/clinical/routers/clinical.py.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  AppointmentOut,
  AppointmentsSummaryOut,
  PatientFull,
  PatientSummary,
  QuoteListItem,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/clinical';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

// ── Agenda ───────────────────────────────────────────────

export async function listAppointments(opts: {
  practitionerId?: number;
  fromDate?: string;        // YYYY-MM-DD
  toDate?: string;
  status?: string;
  limit?: number;
} = {}): Promise<AppointmentOut[]> {
  const params = new URLSearchParams();
  if (opts.practitionerId !== undefined) params.set('practitioner_id', String(opts.practitionerId));
  if (opts.fromDate) params.set('from_date', opts.fromDate);
  if (opts.toDate) params.set('to_date', opts.toDate);
  if (opts.status) params.set('status', opts.status);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/appointments${qs}`, {
    headers: await authHeaders(),
  });
  return asJson<AppointmentOut[]>(res);
}

export async function appointmentsSummary(
  practitionerId?: number,
): Promise<AppointmentsSummaryOut> {
  const qs = practitionerId !== undefined ? `?practitioner_id=${practitionerId}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/appointments/summary${qs}`, {
    headers: await authHeaders(),
  });
  return asJson<AppointmentsSummaryOut>(res);
}

// ── Pacientes ────────────────────────────────────────────

export async function listPatients(q: string = '', limit: number = 50): Promise<PatientSummary[]> {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/patients${qs}`, {
    headers: await authHeaders(),
  });
  return asJson<PatientSummary[]>(res);
}

export async function getPatientRecord(patientId: string): Promise<PatientFull> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/patients/${encodeURIComponent(patientId)}`, {
    headers: await authHeaders(),
  });
  return asJson<PatientFull>(res);
}

// ── Cotizaciones ─────────────────────────────────────────

export async function listQuotes(status?: string, limit = 50): Promise<QuoteListItem[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${DJANGO_PROXY}${BASE}/quotes${qs}`, {
    headers: await authHeaders(),
  });
  return asJson<QuoteListItem[]>(res);
}
